/**
 * actionParser.js - Parse AI responses in multiple formats
 * 
 * Supports:
 * 1. NEW: Pure JSON format with message + actions array (json_with_actions)
 * 2. LEGACY: ---AI_ACTION--- block format
 * 3. LEGACY: ```AI_ACTION:TYPE``` code block format
 * 
 * Action types:
 * - APPLY_UPDATES - Suggest field updates
 * - SUGGEST_SPLIT - Suggest splitting an item
 * - ADD_METRICS - Suggest metrics for epics/goals
 * - AWAIT_FEEDBACK - No action, awaiting user input
 */

/**
 * Normalize action type to expected values
 * AI may return entity-specific types like UPDATE_EPIC, UPDATE_FEATURE
 * We normalize these to APPLY_UPDATES for consistent handling
 */
const normalizeActionType = (type) => {
  if (!type) return 'AWAIT_FEEDBACK';
  
  const normalized = type.toUpperCase();
  
  // Map entity-specific update types to APPLY_UPDATES
  if (normalized.startsWith('UPDATE_') || normalized === 'LINK_PERSONAS') {
    return 'APPLY_UPDATES';
  }
  
  return normalized;
};

/**
 * Extract JSON from a potentially malformed AI response
 * AI models sometimes output preamble text before the JSON object
 * This function finds and extracts only the valid JSON portion
 * @param {string} text - Raw text that may contain JSON
 * @returns {string|null} - Extracted JSON string or null if not found
 */
const extractJSON = (text) => {
  if (!text) return null;
  
  // Find the first `{` character
  const startIndex = text.indexOf('{');
  if (startIndex === -1) return null;
  
  // Find the matching closing `}` by counting braces
  let braceCount = 0;
  let endIndex = -1;
  
  for (let i = startIndex; i < text.length; i++) {
    if (text[i] === '{') braceCount++;
    if (text[i] === '}') braceCount--;
    if (braceCount === 0) {
      endIndex = i;
      break;
    }
  }
  
  if (endIndex === -1) return null;
  
  const extracted = text.substring(startIndex, endIndex + 1);
  
  // Log warning if we had to extract (means AI didn't follow protocol)
  if (startIndex > 0) {
    console.warn('[actionParser] AI response had preamble text before JSON, extracted JSON portion');
  }
  
  return extracted;
};

/**
 * Parse an AI response to extract display message and action data
 * @param {string} response - Raw AI response text (may be JSON or markdown)
 * @returns {{ displayMessage: string, actionData: object|null, actions: array }}
 */
export const parseAIResponse = (response) => {
  if (!response) {
    return { displayMessage: '', actionData: null, actions: [] };
  }

  let displayMessage = response;
  let actionData = null;
  let actions = []; // Raw actions array for Collection co-pilots

  // FIRST: Try to parse as pure JSON (new json_with_actions format)
  // This is the preferred format going forward
  try {
    let cleanedResponse = response.trim();
    
    // Strip markdown code block wrapper if present
    // AI sometimes wraps JSON in ```json ... ```
    const codeBlockMatch = cleanedResponse.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    if (codeBlockMatch) {
      cleanedResponse = codeBlockMatch[1].trim();
    }
    
    // If response doesn't start with {, try to extract JSON from it
    // This handles cases where AI outputs preamble text before JSON
    if (!cleanedResponse.startsWith('{')) {
      const extractedJSON = extractJSON(cleanedResponse);
      if (extractedJSON) {
        cleanedResponse = extractedJSON;
      }
    }
    
    if (cleanedResponse.startsWith('{')) {
      const jsonResponse = JSON.parse(cleanedResponse);
      
      // Check if it has the expected structure
      if (jsonResponse.message !== undefined) {
        displayMessage = jsonResponse.message || '';
        
        // Store raw actions array for Collection co-pilots
        if (jsonResponse.actions && Array.isArray(jsonResponse.actions)) {
          actions = jsonResponse.actions;
        }
        
        // Parse actions for Item co-pilots (actionData format)
        if (actions.length > 0) {
          const firstAction = actions[0];
          actionData = {
            type: normalizeActionType(firstAction.type),
            fields: firstAction.fields || [],
            // Include ALL action properties for Action Registry types (MERGE_ITEMS, CREATE_ITEM, etc.)
            // The entire firstAction becomes the data payload
            data: firstAction
          };
        }
        
        return { displayMessage, actionData, actions };
      }
    }
  } catch {
    // Not valid JSON, continue with legacy parsing
  }

  // LEGACY: Parse ---AI_ACTION--- block format
  const actionMatch = response.match(/---AI_ACTION---\s*([\s\S]*?)\s*---END_ACTION---/);
  
  if (actionMatch) {
    try {
      const actionBlock = actionMatch[1].trim();
      const typeMatch = actionBlock.match(/type:\s*(\w+)/);
      const dataMatch = actionBlock.match(/data:\s*(\{[\s\S]*?\}|\[[\s\S]*?\])/);
      
      if (typeMatch) {
        let parsedData = {};
        if (dataMatch) {
          try {
            parsedData = JSON.parse(dataMatch[1]);
          } catch {
            parsedData = {};
          }
        }
        
        actionData = {
          type: typeMatch[1].toUpperCase(),
          data: parsedData
        };
        
        // Build actions array for Collection co-pilots
        actions = [{ type: typeMatch[1].toUpperCase(), ...parsedData }];
      }
    } catch (e) {
      console.error('Failed to parse AI_ACTION:', e);
    }
    
    // Remove the action block from display message
    displayMessage = response.replace(/---AI_ACTION---[\s\S]*?---END_ACTION---/g, '').trim();
  }
  
  // LEGACY: Check for ```AI_ACTION:TYPE``` code block format
  if (!actionData) {
    const codeBlockPattern = /```AI_ACTION:(\w+)\n([\s\S]*?)```/;
    const codeMatch = response.match(codeBlockPattern);
    
    if (codeMatch) {
      const actionType = codeMatch[1];
      const jsonStr = codeMatch[2].trim();
      
      try {
        const parsedJson = JSON.parse(jsonStr);
        actionData = {
          type: actionType.toUpperCase(),
          data: parsedJson
        };
        
        // Build actions array for Collection co-pilots
        actions = [{ type: actionType.toUpperCase(), ...parsedJson }];
      } catch (err) {
        console.error('Failed to parse AI_ACTION JSON:', err);
      }
      
      displayMessage = response.replace(codeBlockPattern, '').trim();
    }
  }
  
  return { displayMessage, actionData, actions };
};

/**
 * Extract action type from parsed action
 * @param {object} actionData - Parsed action data
 * @returns {string} Action type (APPLY_UPDATES, SUGGEST_SPLIT, etc.)
 */
export const getActionType = (actionData) => {
  return actionData?.type || 'NONE';
};

/**
 * Check if action data contains proposed updates
 * @param {object} actionData - Parsed action data
 * @returns {boolean}
 */
export const hasProposedUpdates = (actionData) => {
  return (
    actionData?.type === 'APPLY_UPDATES' &&
    actionData?.data &&
    Object.keys(actionData.data).length > 0
  );
};

/**
 * Check if action data contains split suggestion
 * @param {object} actionData - Parsed action data
 * @returns {boolean}
 */
export const hasSplitSuggestion = (actionData) => {
  return (
    actionData?.type === 'SUGGEST_SPLIT' &&
    actionData?.data?.feature_1 &&
    actionData?.data?.feature_2
  );
};

/**
 * Check if action data contains metrics suggestion
 * @param {object} actionData - Parsed action data
 * @returns {boolean}
 */
export const hasMetricsSuggestion = (actionData) => {
  return (
    actionData?.type === 'ADD_METRICS' &&
    Array.isArray(actionData?.data?.metrics) &&
    actionData.data.metrics.length > 0
  );
};

/**
 * Get formatted summary of proposed changes
 * @param {object} updates - Proposed field updates
 * @param {object} fieldLabels - Map of field names to display labels
 * @returns {string} Formatted summary
 */
export const getUpdatesSummary = (updates, fieldLabels = {}) => {
  if (!updates || Object.keys(updates).length === 0) {
    return 'No changes proposed';
  }
  
  const fields = Object.keys(updates);
  const labels = fields.map(f => fieldLabels[f] || f);
  
  if (labels.length === 1) {
    return `Update ${labels[0]}`;
  }
  if (labels.length <= 3) {
    return `Update ${labels.join(', ')}`;
  }
  return `Update ${labels.length} fields`;
};

export default parseAIResponse;
